import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../api';
import './Home.css';

function Home({ loggedInUser, onRequireLogin }) {
  const [lobbies, setLobbies] = useState([]);
  const [loggedIn, setLoggedIn] = useState(!!loggedInUser);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('Loading…');
  const [error, setError] = useState(null);
  const lobbiesRef = useRef([]);

  const loadLobbies = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch('/lobbies');
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || 'Could not load lobbies');
      }
      const list = data.lobbies || [];
      setLobbies(list);
      lobbiesRef.current = list;
      setLoggedIn(!!data.loggedIn || !!loggedInUser);

      if (!list.length) {
        setStatus(
          data.loggedIn || loggedInUser
            ? 'No lobbies visible to you'
            : 'No lobbies available',
        );
      } else {
        setStatus(
          data.loggedIn || loggedInUser
            ? 'Pick a lobby to enter.'
            : 'Browse lobbies below — log in to enter one.',
        );
      }
    } catch (err) {
      setError(err.message);
      setStatus('');
    } finally {
      setLoading(false);
    }
  }, [loggedInUser]);

  const refreshCounts = useCallback(async () => {
    const list = lobbiesRef.current;
    if (!list.length) return;
    const ids = list.map((l) => l.id).join(',');
    try {
      const res = await apiFetch(`/lobbies/counts?ids=${encodeURIComponent(ids)}`);
      const data = await res.json().catch(() => ({}));
      if (!data.ok || !data.counts) return;
      setLobbies((prev) => prev.map((lobby) => {
        const id = String(lobby.id);
        const worker = data.workers?.[id];
        return {
          ...lobby,
          onlineCount: data.counts[id] ?? lobby.onlineCount ?? 0,
          workerOnline: worker ? !!worker.online : lobby.workerOnline,
        };
      }));
    } catch {
      /* ignore poll errors */
    }
  }, []);

  useEffect(() => {
    loadLobbies();
  }, [loadLobbies]);

  useEffect(() => {
    if (!lobbies.length) return undefined;
    const timer = setInterval(refreshCounts, 8000);
    return () => clearInterval(timer);
  }, [lobbies.length, refreshCounts]);

  const handleLobbyClick = (e, lobby) => {
    if (lobby?.workerOnline === false) {
      e.preventDefault();
      setStatus('This lobby is offline right now. Try again shortly.');
      return;
    }
    if (!loggedInUser) {
      e.preventDefault();
      setStatus('You must be logged in to open a lobby.');
      onRequireLogin?.();
    }
  };

  return (
    <div className="home-page">
      <header className="home-header">
        <h1>Syber Labs Cards</h1>
        <p className="home-subtitle">Multiplayer card lobbies and table games</p>
      </header>

      <section className="home-section home-lobbies">
        <h2>Lobbies</h2>
        {error && <div className="error-message">{error}</div>}
        {!error && status && <p className="home-status">{loading ? 'Loading…' : status}</p>}

        {!loading && lobbies.length > 0 && (
          <ul className="home-lobby-list">
            {lobbies.map((lobby) => {
              const offline = lobby.workerOnline === false;
              return (
              <li
                key={lobby.id}
                className={['home-lobby-item', offline ? 'home-lobby-item--offline' : ''].filter(Boolean).join(' ')}
              >
                <div className="home-lobby-row">
                  {offline ? (
                    <span
                      className="home-lobby-link home-lobby-link--offline"
                      aria-disabled="true"
                      title="Lobby server offline"
                    >
                      {lobby.title}
                    </span>
                  ) : (
                    <Link
                      to={`/lobby/${lobby.id}`}
                      className="home-lobby-link"
                      onClick={(e) => handleLobbyClick(e, lobby)}
                    >
                      {lobby.title}
                    </Link>
                  )}
                  <span className={['home-lobby-count', offline ? 'home-lobby-count--offline' : ''].filter(Boolean).join(' ')}>
                    {offline ? 'Offline' : `${lobby.onlineCount ?? 0} online`}
                  </span>
                </div>
                {(lobby.description || lobby.slug) && (
                  <p className="home-lobby-desc">{lobby.description || lobby.slug}</p>
                )}
                {offline && (
                  <p className="home-lobby-offline-hint">Lobby server is not running — join is unavailable.</p>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="home-section home-toy">
        <h2>Card toy</h2>
        <p>Try the interactive hand test in your browser.</p>
        <a href="/cards/handtest.html" className="home-toy-link">
          Open Card Toy
        </a>
      </section>
    </div>
  );
}

export default Home;